import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { t, LANGS, LANG_NAMES } from "../i18n/translations";
import { Menu, X, ChevronDown } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_massage-portal-6/artifacts/g3nld1od_logo_transparent_background.webp";

export const Header = () => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", key: "home" },
    { to: "/massagen", key: "massagen" },
    { to: "/klangschalen", key: "klangschalen" },
    { to: "/ernaehrung", key: "ernaehrung" },
    { to: "/gutscheine", key: "vouchers" },
    { to: "/about", key: "about" },
    { to: "/standort", key: "standort" },
  ];

  return (
    <header className="sticky top-0 z-40 bg-background/85 backdrop-blur border-b border-border" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-24 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
          <img src={LOGO} alt="Ayurveda Aargau" className="h-16 lg:h-[72px] w-auto object-contain" />
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {links.map((l) => (
            <NavLink key={l.key} to={l.to} end={l.to === "/"} data-testid={`nav-${l.key}`}
              className={({ isActive }) =>
                `text-[12px] tracking-[0.18em] uppercase transition-colors whitespace-nowrap ${isActive ? "text-terracotta" : "text-ink-muted hover:text-ink"}`
              }>
              {t(lang, `nav.${l.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-border rounded-full hover:bg-muted transition-colors"
              data-testid="lang-switcher-btn"
            >
              <span className="uppercase font-medium tracking-wide">{lang}</span>
              <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 bg-white border border-border rounded-md shadow-md py-1 min-w-[140px] z-50">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    onClick={() => { setLang(l); setLangOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-muted ${lang === l ? "text-terracotta" : ""}`}
                    data-testid={`lang-option-${l}`}
                  >
                    {LANG_NAMES[l]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => navigate("/booking")}
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-white rounded-full text-xs uppercase tracking-wider hover:bg-terracotta-hover transition-colors"
            data-testid="book-cta-header"
          >
            {t(lang, "nav.book")}
          </button>
          <button onClick={() => setOpen(true)} className="xl:hidden p-2" data-testid="mobile-menu-btn" aria-label="Open menu">
            <Menu className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-background xl:hidden animate-fade-in overflow-auto" data-testid="mobile-menu">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <img src={LOGO} alt="Ayurveda Aargau" className="h-14 w-auto object-contain" />
            <button onClick={() => setOpen(false)} className="p-2" aria-label="Close" data-testid="mobile-menu-close">
              <X className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex flex-col p-6 gap-1">
            {links.map((l) => (
              <Link key={l.key} to={l.to} onClick={() => setOpen(false)} className="py-3 text-lg font-display border-b border-border" data-testid={`mobile-nav-${l.key}`}>
                {t(lang, `nav.${l.key}`)}
              </Link>
            ))}
            <Link to="/booking" onClick={() => setOpen(false)} className="mt-6 text-center py-3 bg-terracotta text-white rounded-full" data-testid="mobile-book-cta">
              {t(lang, "nav.book")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
